import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Lock, Truck } from "lucide-react";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { brand } = useOutletContext();
  const { brand: key } = useParams();
  const dark = brand.key === "blackpekoe";
  const fg = dark ? "#F5F2EB" : "#2D2622";
  const muted = dark ? "rgba(245,242,235,0.7)" : "#5C5753";
  const subtle = dark ? "rgba(245,242,235,0.55)" : "#8C8581";
  const cardBg = dark ? "rgba(255,255,255,0.04)" : "white";
  const cardBorder = dark ? "rgba(255,255,255,0.08)" : "rgba(45,38,34,0.1)";

  const { items, subtotal, clear } = useCart();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [address, setAddress] = useState({
    name: user?.name || "", phone: user?.phone || "", line1: "", line2: "",
    city: "", state: "", pincode: "",
  });
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [method, setMethod] = useState("online");
  const [useWallet, setUseWallet] = useState(false);
  const [walletInput, setWalletInput] = useState("");

  useEffect(() => { if (items.length === 0) nav(`/b/${key}/cart`); }, [items.length, nav, key]);

  // Block customers from checking out on the wrong brand
  useEffect(() => {
    if (user?.role === "customer" && user.brand && user.brand !== brand.key) {
      toast.error(`Your account belongs to ${user.brand.toUpperCase()}. Switching back.`);
      nav(`/b/${user.brand}/shop`);
    }
  }, [user, brand.key, nav]);

  const tax = +(subtotal * 0.05).toFixed(2);
  const delivery = subtotal >= 1000 ? 0 : 40;
  const total = +(subtotal + tax + delivery).toFixed(2);

  const walletBalance = Number(user?.wallet_balance || 0);
  const requestedWallet = useWallet ? Math.max(0, Number(walletInput) || 0) : 0;
  const walletApplied = Math.min(requestedWallet, walletBalance, total);
  const payable = +Math.max(0, total - walletApplied).toFixed(2);

  const onChange = (k) => (e) => setAddress({ ...address, [k]: e.target.value });

  const placeOrder = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id, name: i.name, price: i.price,
          unit: i.unit, quantity: i.quantity, subtotal: +(i.price * i.quantity).toFixed(2),
        })),
        address, notes,
        wallet_amount_used: walletApplied,
      };
      const { data: order } = await api.post("/orders", payload);

      // If wallet covered the entire amount, mark as paid via COD path (no gateway)
      if (Number(order.amount_payable || 0) <= 0) {
        await api.post(`/payments/mark-cod/${order.id}`);
        toast.success(`Order ${order.order_no} placed (paid from wallet credit)`);
        clear();
        await refresh();
        nav(`/b/${key}/orders/${order.id}`);
        return;
      }

      if (method === "cod") {
        await api.post(`/payments/mark-cod/${order.id}`);
        toast.success(`Order ${order.order_no} placed! (Cash on Delivery)`);
        clear();
        await refresh();
        nav(`/b/${key}/orders/${order.id}`);
        return;
      }

      const { data: cfg } = await api.get("/payments/config");
      const ok = await loadRazorpay();
      if (!ok) { toast.error("Could not load Razorpay. Try again."); setSubmitting(false); return; }
      if (!order.razorpay_order_id) {
        toast.error("Payment gateway not configured. Use COD or contact support.");
        setSubmitting(false);
        return;
      }

      const rzp = new window.Razorpay({
        key: cfg.key_id,
        amount: Math.round((order.amount_payable || order.total) * 100),
        currency: "INR",
        order_id: order.razorpay_order_id,
        name: brand.name,
        description: `Order ${order.order_no}`,
        prefill: { name: address.name, email: user.email, contact: address.phone },
        theme: { color: brand.primary },
        handler: async (resp) => {
          try {
            await api.post("/payments/verify", {
              order_id: order.id,
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful!");
            clear();
            await refresh();
            nav(`/b/${key}/orders/${order.id}`);
          } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed");
          }
        },
        modal: {
          ondismiss: () => {
            toast("Payment cancelled. Your order is saved as pending.");
            setSubmitting(false);
            nav(`/b/${key}/orders/${order.id}`);
          },
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to place order");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10" style={{ color: fg }}>
      <p className="overline" style={{ color: subtle }}>{brand.name} • Checkout</p>
      <h1 className="font-heading font-bold text-3xl mt-2 mb-8">Delivery &amp; payment</h1>

      <form onSubmit={placeOrder} className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl p-6" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
            <div className="flex items-center gap-2 mb-4">
              <Truck className="w-5 h-5" style={{ color: brand.primary }} />
              <h3 className="font-heading font-semibold text-lg">Delivery address</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Full name</Label><Input data-testid="addr-name" required value={address.name} onChange={onChange("name")} /></div>
              <div><Label>Phone</Label><Input data-testid="addr-phone" required value={address.phone} onChange={onChange("phone")} /></div>
              <div className="sm:col-span-2"><Label>Address line 1</Label><Input data-testid="addr-line1" required value={address.line1} onChange={onChange("line1")} /></div>
              <div className="sm:col-span-2"><Label>Address line 2 (optional)</Label><Input data-testid="addr-line2" value={address.line2} onChange={onChange("line2")} /></div>
              <div><Label>City</Label><Input data-testid="addr-city" required value={address.city} onChange={onChange("city")} /></div>
              <div><Label>State</Label><Input data-testid="addr-state" required value={address.state} onChange={onChange("state")} /></div>
              <div><Label>PIN code</Label><Input data-testid="addr-pin" required value={address.pincode} onChange={onChange("pincode")} /></div>
            </div>
            <div className="mt-4"><Label>Delivery notes (optional)</Label>
              <Textarea data-testid="addr-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Gate code, landmark, etc." />
            </div>
          </div>

          <div className="rounded-xl p-6" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5" style={{ color: brand.primary }} />
              <h3 className="font-heading font-semibold text-lg">Payment method</h3>
            </div>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition"
                style={{ borderColor: method === "online" ? brand.primary : cardBorder, background: method === "online" ? brand.primary + "11" : "transparent" }}>
                <input type="radio" name="m" value="online" checked={method === "online"} onChange={() => setMethod("online")} data-testid="pay-online" className="mt-1" />
                <div>
                  <p className="font-semibold">Pay online via Razorpay</p>
                  <p className="text-sm" style={{ color: muted }}>Cards, UPI, netbanking, wallets — secure checkout.</p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition"
                style={{ borderColor: method === "cod" ? brand.primary : cardBorder, background: method === "cod" ? brand.primary + "11" : "transparent" }}>
                <input type="radio" name="m" value="cod" checked={method === "cod"} onChange={() => setMethod("cod")} data-testid="pay-cod" className="mt-1" />
                <div>
                  <p className="font-semibold">Cash on delivery</p>
                  <p className="text-sm" style={{ color: muted }}>Pay when your order arrives.</p>
                </div>
              </label>
            </div>
          </div>
        </div>

        <aside className="rounded-xl p-6 h-fit sticky top-24" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
          <h3 className="font-heading font-semibold text-lg">Summary</h3>
          <div className="mt-4 space-y-3 text-sm max-h-64 overflow-auto">
            {items.map((it) => (
              <div key={it.product_id} className="flex justify-between gap-2">
                <span className="line-clamp-1" style={{ color: muted }}>{it.name} × {it.quantity}</span>
                <span className="font-semibold whitespace-nowrap">₹{(it.price * it.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-4 space-y-2 text-sm" style={{ borderTop: `1px solid ${cardBorder}` }}>
            <div className="flex justify-between"><span style={{ color: muted }}>Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span style={{ color: muted }}>Tax</span><span>₹{tax.toFixed(2)}</span></div>
            <div className="flex justify-between"><span style={{ color: muted }}>Delivery</span><span>{delivery === 0 ? "Free" : `₹${delivery.toFixed(2)}`}</span></div>
            <div className="flex justify-between font-semibold pt-2" style={{ borderTop: `1px solid ${cardBorder}` }}>
              <span style={{ color: muted }}>Order total</span><span>₹{total.toFixed(2)}</span>
            </div>

            {walletBalance > 0 && (
              <div className="mt-3 p-3 rounded-lg" style={{ background: brand.primary + "11", border: `1px dashed ${brand.primary}66` }}>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="use-wallet-toggle"
                    checked={useWallet}
                    onChange={(e) => {
                      setUseWallet(e.target.checked);
                      if (e.target.checked && !walletInput) {
                        setWalletInput(String(Math.min(walletBalance, total).toFixed(2)));
                      }
                    }}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <p className="font-semibold text-sm">Use wallet credit</p>
                    <p className="text-xs" style={{ color: muted }}>Available: ₹{walletBalance.toFixed(2)}</p>
                    {useWallet && (
                      <div className="mt-2 flex items-center gap-2">
                        <Input
                          data-testid="wallet-amount-input"
                          type="number"
                          step="0.01"
                          min="0"
                          max={Math.min(walletBalance, total)}
                          value={walletInput}
                          onChange={(e) => setWalletInput(e.target.value)}
                          className="h-8 text-sm"
                          style={{ background: cardBg, color: fg }}
                        />
                        <button
                          type="button"
                          data-testid="wallet-use-max"
                          onClick={() => setWalletInput(String(Math.min(walletBalance, total).toFixed(2)))}
                          className="text-xs font-semibold whitespace-nowrap"
                          style={{ color: brand.primary }}
                        >
                          Use max
                        </button>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            )}

            {walletApplied > 0 && (
              <div className="flex justify-between text-sm" style={{ color: "#3D6B42" }}>
                <span>Wallet credit applied</span>
                <span data-testid="wallet-applied" className="font-semibold">− ₹{walletApplied.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 items-baseline" style={{ borderTop: `1px solid ${cardBorder}` }}>
              <span className="font-heading font-semibold">{walletApplied > 0 ? "Amount payable" : "Total"}</span>
              <span data-testid="payable-amount" className="font-heading font-bold text-2xl" style={{ color: brand.primary }}>₹{payable.toFixed(2)}</span>
            </div>
          </div>
          <Button data-testid="place-order-btn" type="submit" disabled={submitting} className="w-full mt-6 text-white" style={{ background: brand.primary }}>
            {submitting ? "Placing…" : payable === 0 ? `Place order (paid from wallet)` : `Place order • ₹${payable.toFixed(2)}`}
          </Button>
        </aside>
      </form>
    </div>
  );
}
